import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useEffect, useCallback, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Input, Card, CardContent, CardHeader } from "@learn-me/ui";
import {
  Settings,
  Save,
  Loader2,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  Video,
  FileText,
} from "lucide-react";
import { api } from "@/lib/api";
import type { ApiResponse } from "@learn-me/types";
import toast from "react-hot-toast";
import ReactPlayer from "react-player";

const chapterConfigSchema = z.object({
  title: z
    .string()
    .min(1, "El título es requerido")
    .max(200, "El título debe tener menos de 200 caracteres"),
  description: z.string().max(1000, "La descripción es muy larga").optional(),
  videoUrl: z
    .string()
    .url("URL de video inválida")
    .optional()
    .or(z.literal("")),
  videoDuration: z.number().min(0).optional().nullable(),
  isFree: z.boolean(),
  isPublished: z.boolean(),
  requiresQuizPass: z.boolean(),
  minQuizScore: z.number().min(0).max(100),
});

type ChapterConfigData = z.infer<typeof chapterConfigSchema>;

interface Chapter {
  id: string;
  title: string;
  description?: string;
  videoUrl?: string;
  videoDuration?: number;
  isFree: boolean;
  isPublished: boolean;
  requiresQuizPass: boolean;
  minQuizScore: number;
  courseId: string;
}

interface ChapterConfigPanelProps {
  chapterId: string;
  onTitleChange?: (title: string) => void;
}

function formatDurationDisplay(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
}

export function ChapterConfigPanel({
  chapterId,
  onTitleChange,
}: ChapterConfigPanelProps) {
  const queryClient = useQueryClient();
  const [isExpanded, setIsExpanded] = useState(true);
  const [showVideoSection, setShowVideoSection] = useState(false);
  const [showQuizSection, setShowQuizSection] = useState(false);

  // Fetch chapter data - only if chapterId is valid
  const { data: chapter, isLoading } = useQuery({
    queryKey: ["chapter-config", chapterId],
    queryFn: async () => {
      // Validate chapterId is a valid ID (UUID or CUID) before making the request
      const isValidId =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          chapterId,
        ) || /^c[a-z0-9]{20,}$/i.test(chapterId);
      if (!chapterId || !isValidId) {
        console.warn("[ChapterConfigPanel] Invalid chapterId:", chapterId);
        return undefined;
      }
      const response = await api.get<ApiResponse<Chapter>>(
        `/chapters/${chapterId}`,
      );
      return response.data.data;
    },
    enabled: !!chapterId,
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setValue,
    watch,
    control,
  } = useForm<ChapterConfigData>({
    resolver: zodResolver(chapterConfigSchema),
    defaultValues: {
      isFree: false,
      isPublished: false,
      requiresQuizPass: false,
      minQuizScore: 70,
    },
  });

  const requiresQuizPass = watch("requiresQuizPass");
  const videoUrl = watch("videoUrl");
  const videoDuration = watch("videoDuration");
  const isPublished = watch("isPublished");
  const title = watch("title");

  // Notify parent of title changes
  useEffect(() => {
    if (title && onTitleChange) {
      onTitleChange(title);
    }
  }, [title, onTitleChange]);

  // Handle video duration detection
  const handleDurationDetected = useCallback(
    (duration: number) => {
      const seconds = Math.round(duration);
      setValue("videoDuration", seconds, { shouldDirty: true });
    },
    [setValue],
  );

  // Load chapter data into form
  useEffect(() => {
    if (chapter) {
      reset({
        title: chapter.title,
        description: chapter.description || "",
        videoUrl: chapter.videoUrl || "",
        videoDuration: chapter.videoDuration || undefined,
        isFree: chapter.isFree,
        isPublished: chapter.isPublished,
        requiresQuizPass: chapter.requiresQuizPass ?? false,
        minQuizScore: chapter.minQuizScore ?? 70,
      });
      // Auto-expand video section if there's a video
      if (chapter.videoUrl) {
        setShowVideoSection(true);
      }
      // Auto-expand quiz section if quiz is required
      if (chapter.requiresQuizPass) {
        setShowQuizSection(true);
      }
    }
  }, [chapter, reset]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: ChapterConfigData) => {
      const response = await api.put<ApiResponse<Chapter>>(
        `/chapters/${chapterId}`,
        data,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["chapter-config", chapterId],
      });
      queryClient.invalidateQueries({ queryKey: ["chapter-edit", chapterId] });
      toast.success("Configuración guardada");
    },
    onError: (
      error: Error & { response?: { data?: { error?: { message?: string } } } },
    ) => {
      const message =
        error.response?.data?.error?.message ||
        "Error al guardar configuración";
      toast.error(message);
    },
  });

  const onSubmit = (data: ChapterConfigData) => {
    updateMutation.mutate(data);
  };

  if (isLoading) {
    return (
      <Card className="border-gray-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-gray-200">
      <CardHeader className="py-3 px-4">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center justify-between w-full"
        >
          <div className="flex items-center gap-2">
            <Settings className="h-4 w-4 text-gray-600" />
            <span className="font-medium text-sm">Configuración</span>
            {isDirty && <span className="w-2 h-2 rounded-full bg-amber-500" />}
          </div>
          <div className="flex items-center gap-2">
            {isPublished ? (
              <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                <Eye className="h-3 w-3" />
                Publicado
              </span>
            ) : (
              <span className="flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                <EyeOff className="h-3 w-3" />
                Borrador
              </span>
            )}
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-400" />
            )}
          </div>
        </button>
      </CardHeader>

      {isExpanded && (
        <CardContent className="px-4 pb-4 pt-0">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Title */}
            <Input
              label="Título del capítulo"
              placeholder="Ej: Introducción al tema"
              error={errors.title?.message}
              {...register("title")}
            />

            {/* Description */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Descripción
              </label>
              <textarea
                className={`w-full px-3 py-2 border rounded-lg text-sm min-h-[60px] resize-none ${
                  errors.description ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Breve descripción..."
                {...register("description")}
              />
              {errors.description && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.description.message}
                </p>
              )}
            </div>

            {/* Toggles */}
            <div className="flex items-center gap-4 py-2 border-y border-gray-100">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  {...register("isPublished")}
                />
                <span className="text-sm">Publicado</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  {...register("isFree")}
                />
                <span className="text-sm">Gratuito</span>
              </label>
            </div>

            {/* Video Section (collapsible) */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setShowVideoSection(!showVideoSection)}
                className="flex items-center justify-between w-full p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Video className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium">Video</span>
                  {videoUrl && (
                    <CheckCircle2 className="h-3 w-3 text-green-500" />
                  )}
                </div>
                {showVideoSection ? (
                  <ChevronUp className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                )}
              </button>

              {showVideoSection && (
                <div className="p-3 space-y-3 border-t border-gray-200">
                  <Input
                    label="URL de Video (YouTube/Vimeo)"
                    placeholder="https://www.youtube.com/watch?v=..."
                    error={errors.videoUrl?.message}
                    {...register("videoUrl")}
                  />

                  {videoUrl && ReactPlayer.canPlay(videoUrl) && (
                    <div className="space-y-2">
                      <div className="aspect-video rounded-lg overflow-hidden bg-black">
                        <ReactPlayer
                          url={videoUrl}
                          width="100%"
                          height="100%"
                          controls
                          light
                          onDuration={handleDurationDetected}
                        />
                      </div>
                      {videoDuration && videoDuration > 0 && (
                        <p className="text-xs text-green-600 flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3" />
                          Duración: {formatDurationDisplay(videoDuration)}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Quiz Section (collapsible) */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setShowQuizSection(!showQuizSection)}
                className="flex items-center justify-between w-full p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium">Quiz</span>
                  {requiresQuizPass && (
                    <CheckCircle2 className="h-3 w-3 text-green-500" />
                  )}
                </div>
                {showQuizSection ? (
                  <ChevronUp className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                )}
              </button>

              {showQuizSection && (
                <div className="p-3 space-y-3 border-t border-gray-200">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-0.5 w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      {...register("requiresQuizPass")}
                    />
                    <div>
                      <span className="text-sm font-medium">
                        Requiere aprobar quiz
                      </span>
                      <p className="text-xs text-gray-500">
                        Estudiantes deben aprobar para continuar
                      </p>
                    </div>
                  </label>

                  {requiresQuizPass && (
                    <div className="ml-7 space-y-2">
                      <label className="block text-sm font-medium">
                        Puntaje mínimo: {watch("minQuizScore")}%
                      </label>
                      <Controller
                        name="minQuizScore"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="range"
                            min="0"
                            max="100"
                            step="5"
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                            value={field.value}
                            onChange={(e) =>
                              field.onChange(Number(e.target.value))
                            }
                          />
                        )}
                      />
                      <div className="flex justify-between text-xs text-gray-400">
                        <span>0%</span>
                        <span>50%</span>
                        <span>100%</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Save button */}
            <Button
              type="submit"
              className="w-full"
              disabled={!isDirty || updateMutation.isPending}
            >
              {updateMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Guardar Configuración
                </>
              )}
            </Button>
          </form>
        </CardContent>
      )}
    </Card>
  );
}
